<!-- src/views/clinical/ClinicalHistoryList.vue -->
<template>
  <div class="clinical-history-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>Historias Clínicas</h1>
      <router-link to="/patients" class="btn btn-primary">
        <i class="bi bi-person-plus me-1"></i> Seleccionar paciente
      </router-link>
    </div>

    <!-- Search and filter bar -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <div class="input-group">
              <span class="input-group-text" id="search-addon">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Buscar paciente..."
                v-model="searchQuery"
                @input="searchHistories"
              />
            </div>
          </div>
          <div class="col-md-6 d-flex justify-content-end">
            <select class="form-select w-auto" v-model="sortBy" @change="sortHistories">
              <option value="patient_asc">Paciente (A-Z)</option>
              <option value="patient_desc">Paciente (Z-A)</option>
              <option value="updated_desc">Última actualización (reciente primero)</option>
              <option value="updated_asc">Última actualización (antiguo primero)</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      Error al cargar las historias clínicas: {{ error }}
    </div>

    <!-- Empty state -->
    <div v-else-if="filteredHistories.length === 0" class="text-center my-5">
      <div class="display-1 text-muted mb-3">
        <i class="bi bi-journal-medical"></i>
      </div>
      <h3 class="text-muted">No se encontraron historias clínicas</h3>
      <p v-if="searchQuery">No hay historias clínicas que coincidan con tu búsqueda "{{ searchQuery }}"</p>
      <p v-else>Comienza seleccionando un paciente para crear su historia clínica</p>
      <router-link to="/patients" class="btn btn-primary mt-3">
        <i class="bi bi-person-plus me-2"></i> Seleccionar paciente
      </router-link>
    </div>

    <!-- History list -->
    <div v-else class="row g-4">
      <div v-for="history in paginatedHistories" :key="history.id" class="col-md-6">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex align-items-center mb-3">
              <div class="avatar-placeholder me-3">
                {{ getInitials(history.patient_name) }}
              </div>
              <div>
                <h5 class="card-title mb-0">
                  {{ history.patient_name }}
                </h5>
                <p class="text-muted mb-0" v-if="history.patient_age">
                  {{ history.patient_age }} años
                </p>
              </div>
            </div>
            
            <div class="mb-3">
              <span class="badge bg-info me-2">
                <i class="bi bi-grid-3x3 me-1"></i> 
                {{ history.has_odontogram ? 'Odontograma disponible' : 'Sin odontograma' }}
              </span>
            </div>
            
            <!-- Allergies section, if present -->
            <div v-if="hasAllergies(history)" class="mb-3">
              <small class="fw-bold text-danger">Alergias: </small>
              <small>{{ getAllergiesFromHistory(history) }}</small>
            </div>
            
            <div class="d-flex justify-content-between mt-3">
              <router-link :to="`/patients/${history.patient}`" class="btn btn-sm btn-outline-primary">
                <i class="bi bi-person me-1"></i> Ver paciente
              </router-link>
              
              <div>
                <router-link 
                  :to="`/clinical-histories/${history.id}/edit`" 
                  class="btn btn-sm btn-outline-secondary me-2"
                >
                  <i class="bi bi-pencil"></i>
                </router-link>
                <router-link 
                  :to="`/clinical-histories/${history.id}`" 
                  class="btn btn-sm btn-primary"
                >
                  Ver <i class="bi bi-arrow-right ms-1"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <nav v-if="totalPages > 1" class="mt-4">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
            <i class="bi bi-chevron-left"></i>
          </a>
        </li>
        
        <li 
          v-for="page in paginationRange" 
          :key="page" 
          class="page-item"
          :class="{ active: currentPage === page }"
        >
          <a class="page-link" href="#" @click.prevent="changePage(page)">
            {{ page }}
          </a>
        </li>
        
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
            <i class="bi bi-chevron-right"></i>
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import { clinicalHistoryService, patientService, odontogramService } from '@/services/api';
import { formatDateForDisplay, calculateAge, formatTime, formatDateForInput } from '@/utils/dateUtils';


export default {
  name: 'ClinicalHistoryList',
  data() {
    return {
      clinicalHistories: [],
      filteredHistories: [],
      enrichedHistories: [],
      loading: true,
      error: null,
      searchQuery: '',
      sortBy: 'patient_asc',
      currentPage: 1,
      itemsPerPage: 6
    };
  },
  computed: {
    paginatedHistories() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredHistories.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredHistories.length / this.itemsPerPage);
    },
    paginationRange() {
      const range = [];
      const delta = 2; // How many pages to show before and after current page
      
      for (let i = Math.max(1, this.currentPage - delta); 
           i <= Math.min(this.totalPages, this.currentPage + delta); 
           i++) {
        range.push(i);
      }
      
      return range;
    }
  },
  async created() {
    try {
      await this.fetchData();
    } catch (error) {
      console.error('Error loading clinical histories:', error);
      this.error = 'No se pudieron cargar las historias clínicas. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchData() {
      // Get clinical histories
      const historiesResponse = await clinicalHistoryService.getAll();
      this.clinicalHistories = historiesResponse.data;
      
      // Get patients to match with clinical histories
      const patientsResponse = await patientService.getAll();
      const patients = patientsResponse.data;
      
      // Get odontograms to check which histories have them
      const odontogramsResponse = await odontogramService.getAll();
      const odontograms = odontogramsResponse.data;
      
      // Enrich histories with patient information
      this.enrichedHistories = this.clinicalHistories.map(history => {
        const patient = patients.find(p => p.id === history.patient);
        const hasOdontogram = odontograms.some(o => o.clinical_history === history.id);
        
        // Calculate age if birth date is available
        let patientAge = null;
        if (patient && patient.fecha_nacimiento) {
          const birthDate = new Date(patient.fecha_nacimiento);
          const today = new Date();
          patientAge = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            patientAge--;
          }
        }
        
        return {
          ...history,
          patient_name: patient ? `${patient.first_name} ${patient.last_name}` : 'Paciente desconocido',
          patient_age: patientAge,
          has_odontogram: hasOdontogram
        };
      });
      
      this.filteredHistories = [...this.enrichedHistories];
      this.sortHistories();
    },
    searchHistories() {
      if (!this.searchQuery.trim()) {
        this.filteredHistories = [...this.enrichedHistories];
      } else {
        const query = this.searchQuery.toLowerCase().trim();
        this.filteredHistories = this.enrichedHistories.filter(history => {
          return history.patient_name.toLowerCase().includes(query) || 
                 (history.notes && history.notes.toLowerCase().includes(query));
        });
      }
      
      this.currentPage = 1; // Reset to first page on search
      this.sortHistories();
    },
    sortHistories() {
      switch (this.sortBy) {
        case 'patient_asc':
          this.filteredHistories.sort((a, b) => a.patient_name.localeCompare(b.patient_name));
          break;
        case 'patient_desc':
          this.filteredHistories.sort((a, b) => b.patient_name.localeCompare(a.patient_name));
          break;
        case 'updated_desc':
          this.filteredHistories.sort((a, b) => {
            if (!a.updated_at) return 1;
            if (!b.updated_at) return -1;
            return new Date(b.updated_at) - new Date(a.updated_at);
          });
          break;
        case 'updated_asc':
          this.filteredHistories.sort((a, b) => {
            if (!a.updated_at) return 1;
            if (!b.updated_at) return -1;
            return new Date(a.updated_at) - new Date(b.updated_at);
          });
          break;
      }
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    getInitials(fullName) {
      if (!fullName) return '??';
      
      const parts = fullName.split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return parts[0].charAt(0).toUpperCase();
    },
    formatDate(dateString) {
      return formatDateForDisplay(dateString);
    },
    cleanHistoryNotes(notes) {
  if (!notes) return '';
  
  // Clean the text by removing JSON sections
  let cleanedText = notes;
  
  // Remove the standard custom fields section
  cleanedText = cleanedText.replace(/__CUSTOM_FIELDS__:[\s\S]*?__END_CUSTOM_FIELDS__/g, '');
  
  // Remove any alternate custom fields format that might be present
  cleanedText = cleanedText.replace(/_+CUSTOM_FIELDS_+\s*:[^}]*}/g, '');
  
  // Remove treatment data JSON arrays if present
  cleanedText = cleanedText.replace(/\[\s*\{\s*"date"[\s\S]*?\}\s*\]/g, '');
  
  // Remove any other JSON objects that might be present
  cleanedText = cleanedText.replace(/\{\s*"allergies"[\s\S]*?\}/g, '');
  
  // Trim and limit the length
  cleanedText = cleanedText.trim();
  if (cleanedText.length > 150) {
    cleanedText = cleanedText.substring(0, 147) + '...';
  }
  
  return cleanedText || 'No hay notas clínicas registradas';
},
    hasAllergies(history) {
      if (!history.notes) return false;
      
      try {
        // Check for standard format
        const standardMatch = history.notes.match(/__CUSTOM_FIELDS__:\n([\s\S]*?)__END_CUSTOM_FIELDS__/);
        if (standardMatch && standardMatch[1]) {
          const customFields = JSON.parse(standardMatch[1].trim());
          return customFields.allergies && customFields.allergies.length > 0;
        }
        
        // Check for alternate format
        const alternateMatch = history.notes.match(/_+CUSTOM_FIELDS_+\s*:\s*(\{[\s\S]*?\})/);
        if (alternateMatch && alternateMatch[1]) {
          const customFields = JSON.parse(alternateMatch[1].trim());
          return customFields.allergies && customFields.allergies.length > 0;
        }
      } catch (e) {
        console.error('Error parsing custom fields for allergies:', e);
      }
      
      return false;
    },
    getAllergiesFromHistory(history) {
      if (!history.notes) return 'Ninguna registrada';
      
      try {
        // Try standard format first
        const standardMatch = history.notes.match(/__CUSTOM_FIELDS__:\n([\s\S]*?)__END_CUSTOM_FIELDS__/);
        if (standardMatch && standardMatch[1]) {
          const customFields = JSON.parse(standardMatch[1].trim());
          if (customFields.allergies && customFields.allergies.length > 0) {
            return customFields.allergies.join(', ');
          }
        }
        
        // Try alternate format
        const alternateMatch = history.notes.match(/_+CUSTOM_FIELDS_+\s*:\s*(\{[\s\S]*?\})/);
        if (alternateMatch && alternateMatch[1]) {
          const customFields = JSON.parse(alternateMatch[1].trim());
          if (customFields.allergies && customFields.allergies.length > 0) {
            return customFields.allergies.join(', ');
          }
        }
      } catch (e) {
        console.error('Error parsing custom fields for allergies:', e);
      }
      
      return 'Ninguna registrada';
    }
  }
};
</script>

<style scoped>
.history-notes {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #495057;
}
</style>